import { ChevronRight, Check } from 'lucide-react'
import { getPriority, getSubtitle, adaptItemForDisplay } from '../../lib/itemPriority'
import { formatQuantity } from '../../lib/itemDefaults'

type Props = {
  item:         any
  onClick:      () => void
  selectMode?:  boolean
  selected?:    boolean
  onToggle?:    () => void
  onAdjustQty?: (item: any) => void
}

export function ItemRow({
  item, onClick,
  selectMode = false,
  selected   = false,
  onToggle,
  onAdjustQty,
}: Props) {
  const adapted  = adaptItemForDisplay(item)
  const priority = getPriority(adapted)
  const subtitle = getSubtitle(adapted, priority)

  const dotClass =
    priority === 'critical' ? 'bg-terracotta' :
    priority === 'warn'     ? 'bg-sand'        :
                              'bg-[#C5BFB8]'

  // In select mode, tap toggles selection; otherwise opens detail.
  const handleClick = selectMode ? (onToggle ?? (() => {})) : onClick

  const qty = formatQuantity(item.current_quantity, item.quantity_unit)
  // Pill is interactive only outside select mode (selection wins) and only
  // when an adjust handler is wired in. Otherwise renders as plain text.
  const pillIsTappable = !selectMode && !!onAdjustQty && !!qty

  return (
    <button
      onClick={handleClick}
      className={`w-full flex items-center gap-3 px-4 py-[14px] border-b border-border-hairline text-left active:bg-cream-warm ${
        selectMode && selected
          ? 'bg-ok-bg/40'
          : priority === 'critical'
            ? 'bg-cream-warm'
            : 'bg-paper'
      }`}
    >
      {selectMode ? (
        <div
          className={`h-6 w-6 rounded-full flex-shrink-0 flex items-center justify-center transition ${
            selected
              ? 'bg-forest text-forest-ink'
              : 'border-[1.5px] border-border-avatar bg-paper'
          }`}
          aria-hidden
        >
          {selected && <Check className="h-3 w-3" strokeWidth={2.5} />}
        </div>
      ) : (
        <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${dotClass}`} />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium text-ink leading-tight truncate">
          {item.name}
          {qty && (
            pillIsTappable ? (
              <span
                role="button"
                tabIndex={-1}
                aria-label={`Adjust quantity, currently ${qty}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onAdjustQty!(item)
                }}
                className="ml-1.5 inline-flex items-center font-serif text-[12px] italic text-ink-muted tracking-normal active:text-ink rounded-md px-1 -mx-1 cursor-pointer"
              >
                {qty}
              </span>
            ) : (
              <span className="ml-1.5 font-serif text-[12px] italic text-ink-muted tracking-normal">
                {qty}
              </span>
            )
          )}
        </p>
        <p className="mt-0.5 text-[11px] text-ink-muted leading-tight">{subtitle}</p>
      </div>
      {!selectMode && <ChevronRight className="h-4 w-4 text-ink-muted shrink-0" strokeWidth={1.5} />}
    </button>
  )
}
